import { STATUS_CONFIG, EXAM_TYPE_LABEL, PRESCRIPTION_WARNING_LABELS, guidelineText } from "../../utils";

const DAY_LABELS = {
  MONDAY: "Lundi",
  TUESDAY: "Mardi",
  WEDNESDAY: "Mercredi",
  THURSDAY: "Jeudi",
  FRIDAY: "Vendredi",
  SATURDAY: "Samedi",
  SUNDAY: "Dimanche",
};

const FLAG_LABELS = {
  PATIENT_INFO_ALREADY_EXISTS: "Patient déjà existant",
  PATIENT_NAME_MISSING_IN_PRESCRIPTION: "Nom patient manquant",
  NOT_ELIGIBLE: "Non éligible",
  DUPLICATE: "Doublon",
};

const TRIGGER_INLINE_OPERATOR = {
  CODE_SOME: "OR",
  CODE_EVERY: "AND",
  EXAM_TYPE_SOME: "OR",
  EXAM_TYPE_EVERY: "AND",
  MUTUELLE_CODE: "OR",
  QUESTION: "OR",
};

const questionLabel = (question) => question.question || question.questionCode || "Question";

const guidelineLabel = (guideline) => guidelineText(guideline) || guideline.guidelineCode || "Recommandation";

const answerLabel = (option) => option.label || option.code || "Réponse";

const actionLabel = (action) => action.name?.trim() || "Action";

const infoTriggerLabel = (trigger) => {
  switch (trigger.field) {
    case "SEXE":
      return `Sexe : ${(trigger.values || []).map((v) => (v === "M" ? "Homme" : "Femme")).join(" / ") || "?"}`;
    case "AGE":
      return `Âge ${trigger.values?.[0] ?? "?"}–${trigger.values?.[1] ?? "?"} ans`;
    case "INS":
      return `INS ${(trigger.values || []).includes("QUALIFIED") ? "qualifié" : "non qualifié"}`;
    case "PATIENT_STATUS":
      return `Patient ${(trigger.values || []).includes("KNOWN") ? "connu" : "nouveau"}`;
    case "PRESCRIPTION":
      return `Ordonnance ${(trigger.values || []).includes("PRESENT") ? "présente" : "absente"}`;
    case "EXAM":
      return (trigger.values || []).includes("DETECTED") ? "Examen détecté sur au moins une ordonnance" : "Au moins une ordonnance sans examen détecté";
    case "PRESCRIPTION_WARNING":
      return `Alerte ordonnance : ${(trigger.values || []).map((w) => PRESCRIPTION_WARNING_LABELS[w] || w).join(" / ") || "?"}`;
    case "TYPE":
      return `Prélèvement ${(trigger.values || []).includes("NOW") ? "au laboratoire" : "au domicile"}`;
    case "MUTUELLE": {
      const v = trigger.values || [];
      if (v.includes("UP_TO_DATE")) return "Mutuelle à jour";
      if (v.includes("OUTDATED")) return "Mutuelle périmée";
      if (v.includes("UNKNOWN")) return "Mutuelle non renseignée";
      return "Mutuelle ?";
    }
    case "DAY":
      return `Jour : ${(trigger.values || []).map((d) => DAY_LABELS[d] || d).join(" / ") || "?"}`;
    case "HOUR":
      return `Heure ${trigger.values?.[0] || "?"}–${trigger.values?.[1] || "?"}`;
    default:
      return trigger.field || "Info patient";
  }
};

const deltaTriggerLabel = (trigger) => {
  if (trigger.field === "DATETIME") {
    const unitMap = { MINUTES: "min", HOURS: "h", DAYS: "j" };
    const operator = trigger.values?.[4] || ">=";
    const threshold = trigger.values?.[5] || "?";
    const unit = unitMap[trigger.values?.[6]] || "min";
    return `Δ date+heure ${operator} ${threshold}${unit}`;
  }
  const operator = trigger.values?.[2] || ">=";
  const threshold = trigger.values?.[3] || "?";
  const unit = trigger.field === "DATE" ? "j" : "min";
  return `Δ ${trigger.field === "DATE" ? "date" : "heure"} ${operator} ${threshold}${unit}`;
};

const codeNodeId = (code) => `code:${code}`;
const mutuelleCodeNodeId = (code) => `code:mutuelle:${code}`;
const questionNodeId = (id) => `question:${id}`;
const guidelineNodeId = (id) => `guideline:${id}`;
const actionNodeId = (id) => `action:${id}`;
const answerNodeId = (questionId, optionId) => `answer:${questionId}:${optionId}`;

export const anchorIdFromProps = ({ currentQuestionId, currentGuidelineId, currentActionId, currentCodes }) => {
  if (currentQuestionId) return questionNodeId(currentQuestionId);
  if (currentGuidelineId) return guidelineNodeId(currentGuidelineId);
  if (currentActionId) return actionNodeId(currentActionId);
  if (currentCodes?.length === 1) return codeNodeId(currentCodes[0]);
  return null;
};

export const codeAnchorIds = (currentCodes) => (currentCodes || []).filter(Boolean).map(codeNodeId);

// Builds a directed cause→effect graph from the full {questions, guidelines, actions} dataset.
// Every entity / code / answer is a single deduped node, so reverse lookups ("who uses this code")
// come for free from the in/out adjacency.
export const buildDependencyGraph = (data) => {
  const { questions = [], guidelines = [], actions = [] } = data || {};
  const nodes = new Map();
  const edgeMap = new Map();
  const questionsById = new Map(questions.map((q) => [String(q._id), q]));

  const addNode = (id, node) => {
    if (!nodes.has(id)) nodes.set(id, { id, ...node });
    return id;
  };

  const addEdge = (source, target, edge) => {
    if (!source || !target || source === target) return;
    const key = `${source}->${target}#${edge.kind}#${edge.triggerKey || edge.effectOp || ""}`;
    if (edgeMap.has(key)) return;
    edgeMap.set(key, { id: key, source, target, ...edge });
  };

  const addCodeNode = (code) => addNode(codeNodeId(code), { kind: "code", label: code, navigable: true });

  const addContextNode = (id, facet, label) => addNode(id, { kind: "context", facet, label });

  const addExamTypeNode = (type) => addContextNode(`examtype:${type}`, "type", EXAM_TYPE_LABEL[type] || type);

  const ensureQuestionNode = (question) => {
    const id = questionNodeId(question._id);
    if (nodes.has(id)) return id;
    addNode(id, { kind: "question", label: questionLabel(question), navigable: true, editType: "question", raw: question });
    if (question.answerType === "MULTIPLE_CHOICE") {
      for (const option of question.options || []) {
        const ansId = answerNodeId(question._id, option._id);
        addNode(ansId, { kind: "answer", label: answerLabel(option), sublabel: questionLabel(question), parentId: id });
        addEdge(id, ansId, { kind: "owns" });
      }
    }
    return id;
  };

  // Resolves a single trigger to its source node id(s), creating nodes as needed.
  const triggerSourceIds = (trigger) => {
    switch (trigger.type) {
      case "CODE_SOME":
      case "CODE_EVERY":
        return (trigger.values || []).filter(Boolean).map(addCodeNode);
      case "EXAM_TYPE_SOME":
      case "EXAM_TYPE_EVERY":
        return (trigger.values || []).filter(Boolean).map(addExamTypeNode);
      case "MUTUELLE_CODE":
        return (trigger.values || [])
          .filter(Boolean)
          .map((code) => addNode(mutuelleCodeNodeId(code), { kind: "code", variant: "mutuelle", label: `Mutuelle ${code}`, navigable: true }));
      case "QUESTION": {
        const srcQuestion = questionsById.get(String(trigger.field));
        if (!srcQuestion) return [];
        ensureQuestionNode(srcQuestion);
        return (trigger.values || [])
          .map((optId) => (srcQuestion.options || []).find((o) => String(o._id) === String(optId)))
          .filter(Boolean)
          .map((option) => answerNodeId(srcQuestion._id, option._id));
      }
      case "INFO": {
        const facet = ["SEXE", "AGE", "INS", "PATIENT_STATUS", "MUTUELLE"].includes(trigger.field) ? "patient" : trigger.field === "HOUR" || trigger.field === "DAY" ? "temporal" : "admission";
        return [addContextNode(`info:${trigger.field}:${(trigger.values || []).join("_")}`, facet, infoTriggerLabel(trigger))];
      }
      case "LABORATORY": {
        const count = (trigger.values || []).length;
        return [addContextNode(`lab:${(trigger.values || []).join("_")}`, "laboratory", count === 1 ? "1 laboratoire" : `${count} laboratoires`)];
      }
      case "FLAG": {
        const flag = (trigger.values || [])[0];
        return [addContextNode(`flag:${(trigger.values || []).join("_")}`, "flag", `Alerte : ${FLAG_LABELS[flag] || flag || "?"}`)];
      }
      case "STATUS": {
        const label = (trigger.values || []).map((v) => STATUS_CONFIG[v]?.label || v).join(", ") || "?";
        return [addContextNode(`status:${(trigger.values || []).join("_")}`, "status", `Statut : ${label}`)];
      }
      case "SOURCE":
        return [addContextNode(`source:${(trigger.values || []).join("_")}`, "source", `Source : ${(trigger.values || []).includes("IDE") ? "IDE" : "Patient"}`)];
      case "DELTA": {
        const deltaId = addContextNode(`delta:${trigger.field}:${(trigger.values || []).join("_")}`, "temporal", deltaTriggerLabel(trigger));
        // A delta source slot can be a question answer ("QUESTION:<id>") instead of submission date/time;
        // wire those source questions into the delta node so the question→entity path stays visible.
        const sourceIndexes = trigger.field === "DATETIME" ? [0, 1, 2, 3] : [0, 1];
        for (const index of sourceIndexes) {
          const raw = (trigger.values || [])[index];
          if (typeof raw !== "string" || !raw.startsWith("QUESTION:")) continue;
          const srcQuestion = questionsById.get(raw.slice(9));
          if (!srcQuestion) continue;
          ensureQuestionNode(srcQuestion);
          addEdge(questionNodeId(srcQuestion._id), deltaId, {
            kind: "trigger",
            groupKey: `${deltaId}#sources`,
            groupIndex: 0,
            groupOperator: "AND",
            groupsOperator: "AND",
            triggerKey: `${deltaId}#src:${srcQuestion._id}`,
            triggerIndex: index,
            inlineOperator: null,
            negate: false,
          });
        }
        return [deltaId];
      }
      case "ALWAYS":
        return [addContextNode("always", "always", "Dans tous les cas")];
      default:
        return [];
    }
  };

  const processTriggers = (entity, targetId) => {
    const groups = (entity.triggerGroups || []).filter((g) => g.triggers?.length > 0);
    const groupsOperator = entity.groupsOperator === "OR" ? "OR" : "AND";
    groups.forEach((group, groupIndex) => {
      const groupOperator = group.operator === "OR" ? "OR" : "AND";
      const groupKey = `${targetId}#g${groupIndex}`;
      group.triggers.forEach((trigger, triggerIndex) => {
        const triggerKey = `${groupKey}#t${triggerIndex}`;
        // How a single trigger's own values combine: CODE_SOME "au moins un" → OU, CODE_EVERY "tous" → ET, etc.
        const inlineOperator = TRIGGER_INLINE_OPERATOR[trigger.type] || null;
        for (const sourceId of triggerSourceIds(trigger)) {
          addEdge(sourceId, targetId, { kind: "trigger", groupKey, groupIndex, groupOperator, groupsOperator, triggerKey, triggerIndex, inlineOperator, negate: trigger.negate === true });
        }
      });
    });
  };

  for (const question of questions) {
    ensureQuestionNode(question);
    processTriggers(question, questionNodeId(question._id));
  }

  for (const guideline of guidelines) {
    const id = addNode(guidelineNodeId(guideline._id), { kind: "guideline", label: guidelineLabel(guideline), navigable: true, editType: "guideline", raw: guideline });
    processTriggers(guideline, id);
  }

  for (const action of actions) {
    const id = addNode(actionNodeId(action._id), { kind: "action", label: actionLabel(action), navigable: true, editType: "action", raw: action });
    processTriggers(action, id);
    for (const effect of action.effects || []) {
      if (effect.type === "ADD_CODES") for (const code of effect.values || []) addEdge(id, addCodeNode(code), { kind: "effect", effectOp: "ADD" });
      if (effect.type === "REMOVE_CODES") for (const code of effect.values || []) addEdge(id, addCodeNode(code), { kind: "effect", effectOp: "REMOVE" });
      if (effect.type === "REPLACE_CODES") {
        for (const code of effect.values || []) addEdge(id, addCodeNode(code), { kind: "effect", effectOp: "REPLACE_OUT" });
        for (const code of effect.options?.replaceWith || []) addEdge(id, addCodeNode(code), { kind: "effect", effectOp: "REPLACE_IN" });
      }
      if (effect.type === "REMOVE_ALL_CODES_EXCEPT") for (const code of effect.values || []) addEdge(id, addCodeNode(code), { kind: "effect", effectOp: "KEEP_EXCEPT" });
      if (effect.type === "REMOVE_CODES_OF_TYPE") for (const type of effect.values || []) addEdge(id, addExamTypeNode(type), { kind: "effect", effectOp: "REMOVE_TYPE" });
      if (effect.type === "REMOVE_ALL_CODES_EXCEPT_OF_TYPE") for (const type of effect.values || []) addEdge(id, addExamTypeNode(type), { kind: "effect", effectOp: "KEEP_TYPE" });
      if (effect.type === "DUPLICATE_ADMISSION") {
        const name = effect.options?.duplicateName?.trim();
        const dupId = addContextNode(`dup:${action._id}`, "duplicate", name ? `Duplique « ${name} »` : "Duplique le dossier");
        addEdge(id, dupId, { kind: "effect", effectOp: "DUPLICATE" });
      }
      if (effect.type === "ANSWER_QUESTION" && effect.options?.questionId) {
        const srcQuestion = questionsById.get(String(effect.options.questionId));
        if (srcQuestion) {
          ensureQuestionNode(srcQuestion);
          addEdge(id, questionNodeId(srcQuestion._id), { kind: "effect", effectOp: "ANSWER", effectLabel: effect.options.answer ? `« ${effect.options.answer} »` : "" });
        }
      }
    }
  }

  const inEdges = new Map();
  const outEdges = new Map();
  for (const edge of edgeMap.values()) {
    if (!outEdges.has(edge.source)) outEdges.set(edge.source, []);
    if (!inEdges.has(edge.target)) inEdges.set(edge.target, []);
    outEdges.get(edge.source).push(edge);
    inEdges.get(edge.target).push(edge);
  }

  return { nodes, inEdges, outEdges };
};
